import { buildService } from './service';
import serviceMethods from './service_methods';
import { useCallback } from 'react';
import confirmationServiceModule from './confirmation.service';

// Build services for different auth endpoints - all under /api now
const userService = buildService('/users', { only: ['create'] });
const confirmationService = buildService('/users/confirmation', { only: ['create'] });
const passwordService = buildService('/users/password', { only: ['create', 'update'] });

// Simple API call for session-based auth
const api = async (url, options = {}) => {
  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  const fullUrl = `${baseUrl}${url}`;

  return fetch(fullUrl, {
    ...options,
    credentials: 'include', // Always include cookies for session
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
};

// Check current user authentication status
const checkAuthStatus = async () => {
  try {
    console.log('🔍 AuthService: Checking auth status...');
    
    const response = await api('/api/current_user', {
      method: 'GET'
    });

    if (!response.ok) {
      throw new Error(`Auth check failed: ${response.status}`);
    }

    const result = await response.json();
    console.log('✅ AuthService: Auth check successful', result);
    return result.user;
  } catch (error) {
    console.log('❌ AuthService: Auth check failed (user not logged in)', error.message);
    return null;
  }
};

// Hook-based methods for components
const useLogin = ({ setLoading, onSuccess, onError }) => {
  const callback = useCallback(
    async (credentials) => {
      console.log('🔐 AuthService: useLogin callback started', { email: credentials.email });
      
      try {
        const response = await api('/api/users/sign_in', {
          method: 'POST',
          body: JSON.stringify({ user: credentials }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Login failed');
        }

        const result = await response.json();
        console.log('✅ AuthService: Login API call successful', result);
        
        return {
          user: result.user
        };
      } catch (error) {
        console.error('❌ AuthService: Login API call failed', error);
        throw error;
      }
    },
    []
  );

  return serviceMethods.useCreate({
    callback,
    setLoading,
    onSuccess: (result) => {
      console.log('🎉 AuthService: useLogin onSuccess called', result);
      if (onSuccess) onSuccess(result);
    },
    onError: (error) => {
      console.error('💥 AuthService: useLogin onError called', error);
      const errorMessage = error?.message || error?.error || error || 'Login failed';
      if (onError) {
        onError({ message: errorMessage });
      }
    }
  });
};

const useResendConfirmation = ({ setLoading, onSuccess, onError }) => {
  return confirmationServiceModule.useResendConfirmation({
    setLoading,
    onSuccess,
    onError
  });
};

const useRegister = ({ setLoading, onSuccess, onError }) => {
  const callback = useCallback(
    async (userData) => {
      console.log('👤 AuthService: Registering user', { email: userData.email });
      const result = await userService.create({ user: userData });
      console.log('✅ AuthService: Registration successful', result);
      return result;
    },
    []
  );

  return serviceMethods.useCreate({
    callback,
    setLoading,
    onSuccess,
    onError
  });
};

const useForgotPassword = ({ setLoading, onSuccess, onError }) => {
  const callback = useCallback(
    async (userData) => {
      console.log('🔑 AuthService: Requesting password reset for', userData.email);
      const result = await passwordService.create({ user: userData });
      console.log('✅ AuthService: Password reset requested', result);
      return result;
    },
    []
  );

  return serviceMethods.useCreate({
    callback,
    setLoading,
    onSuccess,
    onError
  });
};

const useResetPassword = ({ setLoading, onSuccess, onError }) => {
  const callback = useCallback(
    async (passwordData) => {
      console.log('🔒 AuthService: Resetting password with token');
      
      const response = await api('/api/users/password', {
        method: 'PUT',
        body: JSON.stringify({ user: passwordData }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.status?.message || errorData.error || 'Password reset failed');
      }

      const result = await response.json();
      console.log('✅ AuthService: Password reset successful', result);
      return result;
    },
    []
  );

  return serviceMethods.useUpdate({
    callback,
    setLoading,
    onSuccess,
    onError
  });
};

// Direct methods for AuthContext (non-hook versions)
const login = async (email, password) => {
  console.log('🔐 AuthService: Direct login call started', { email });
  
  try {
    const response = await api('/api/users/sign_in', {
      method: 'POST',
      body: JSON.stringify({ user: { email, password } }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Login failed');
    }

    const result = await response.json();
    console.log('✅ AuthService: Direct login successful', result);
    
    return {
      user: result.user
    };
  } catch (error) {
    console.error('❌ AuthService: Direct login failed', error);
    throw error;
  }
};

const register = async (email, password) => {
  console.log('👤 AuthService: Direct register call', { email });
  const result = await userService.create({ user: { email, password } });
  console.log('✅ AuthService: Direct register successful', result);
  return result;
};

const resendConfirmation = async (email) => {
  console.log('📧 AuthService: Direct resend confirmation', email);
  const result = await confirmationService.create({ user: { email } });
  console.log('✅ AuthService: Direct resend successful', result);
  return result;
};

const logout = async () => {
  console.log('🚪 AuthService: Logout started');
  try {
    const response = await api('/api/users/sign_out', {
      method: 'DELETE'
    });
    
    if (!response.ok) {
      throw new Error(`Logout failed: ${response.status}`);
    }
    
    console.log('✅ AuthService: Logout successful');
  } catch (error) {
    console.error('❌ AuthService: Logout error', error);
    // Don't re-throw error - session will be cleared either way
  }
};

// Updated methods for AuthContext
const getCurrentUser = async () => {
  console.log('👤 AuthService: Getting current user');
  return await checkAuthStatus();
};

const isAuthenticated = async () => {
  console.log('🔍 AuthService: Checking if authenticated');
  const user = await checkAuthStatus();
  const result = user !== null;
  console.log('✅ AuthService: Authentication status:', result);
  return result;
};

const authService = {
  // Hook-based methods for components
  useLogin,
  useResendConfirmation,
  useRegister,
  useForgotPassword,
  useResetPassword,
  
  // Direct methods for AuthContext
  login,
  register,
  resendConfirmation,
  logout,
  getCurrentUser,
  isAuthenticated,
  checkAuthStatus,
  
  // Simple API helper
  api
};

export default authService; 
